// Plain-SVG cold point chart: safe-range band, excursion shading, gaps shaded and NEVER bridged,
// min/max envelope + average line, door marks, moving/stopped strip (spec §9).
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateTimeInTz, orgTimezone } from '@/features/staff/attendanceModel';
import { buildChart, probeAt } from './coldChartModel';

const fmt1 = (v) => (typeof v === 'number' ? v.toFixed(1) : '—');

/**
 * `interactive` (default) adds a hover / touch crosshair with a time + temperature tooltip.
 * `onActivate` makes the whole chart a button (click / Enter) — the Live Map opens the cold point drawer.
 * `tz` is the org timezone (callers pass it; unset falls back to the platform default).
 */
export default function ColdPointChart({ history, height = 220, compact = false, tz = null, interactive = true, onActivate = null }) {
  const { t } = useTranslation();
  const zone = tz ?? orgTimezone(null);
  const hatchId = `cc-hatch-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;   // one chart per card/report row: ids must not collide
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [probe, setProbe] = useState(null);   // { vx, ...probeAt }
  // Touch: the first tap shows the reading (and stays), a second tap on the chart activates it.
  const touchArmed = useRef(false);
  const lastType = useRef('mouse');
  const touchProbe = probe && lastType.current === 'touch';
  useEffect(() => {
    if (!touchProbe) return undefined;
    const away = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      touchArmed.current = false;
      setProbe(null);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [touchProbe]);
  const chart = useMemo(() => buildChart({
    buckets: history.buckets ?? [],
    gaps: history.gaps ?? [],
    doors: history.doors ?? [],
    moving: history.moving ?? [],
    excursions: history.excursions ?? [],
    rangeMin: history.point?.rangeMin ?? null,
    rangeMax: history.point?.rangeMax ?? null,
    fromMs: history.fromMs,
    toMs: history.toMs,
    segments: history.segments ?? [],
    height,
    ...(compact ? { pad: { l: 4, r: 4, t: 4, b: 8 } } : {}),
  }), [history, height, compact]);
  const { plot, strip } = chart;
  const spanMs = Math.max(1, history.toMs - history.fromMs);

  const probeFromEvent = (e) => {
    lastType.current = e.pointerType === 'touch' ? 'touch' : 'mouse';
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    const vx = Math.min(Math.max(((e.clientX - rect.left) / rect.width) * chart.width, plot.x), plot.x + plot.w);
    const ms = history.fromMs + ((vx - plot.x) / plot.w) * spanMs;
    const hit = probeAt(history, ms, spanMs * 0.015);
    setProbe({ ...hit, vx: hit.kind === 'value' ? plot.x + ((hit.atMs - history.fromMs) / spanMs) * plot.w : vx });
  };
  const tipText = probe && (probe.kind === 'value'
    ? (probe.min === probe.max ? `${fmt1(probe.avg)} °C` : `${fmt1(probe.avg)} °C (${fmt1(probe.min)}–${fmt1(probe.max)})`)
    : probe.kind === 'off' ? t('coldChain.chart.off', 'Monitoring off') : t('coldChain.chart.noData', 'No data'));
  const doorText = probe?.door && (probe.door.open ? t('coldChain.chart.doorOpen', 'Door open') : t('coldChain.chart.doorClosed', 'Door closed'));
  const pct = probe ? Math.min(Math.max((probe.vx / chart.width) * 100, 0), 100) : 0;
  // Anchor by side near the edges so the tooltip never overflows the chart (all in the svg's ltr space).
  const tipShift = pct < 25 ? '0%' : pct > 75 ? '-100%' : '-50%';
  const onClick = () => {
    if (lastType.current === 'touch' && !touchArmed.current) { touchArmed.current = true; return; }
    touchArmed.current = false;
    onActivate();
  };
  const spark = !interactive;   // sparkline: fixed pixel height at any width

  const svg = (
    <svg ref={svgRef} viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full" style={spark ? { height, touchAction: 'pan-y' } : { maxHeight: height, touchAction: 'pan-y' }} dir="ltr"
      {...(spark ? { preserveAspectRatio: 'none' } : {})}
      {...(onActivate
        ? { role: 'button', tabIndex: 0, onClick, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } } }
        : { role: 'img' })}
      {...(interactive ? { onPointerMove: probeFromEvent, onPointerDown: probeFromEvent, onPointerLeave: (e) => { if (e.pointerType !== 'touch') setProbe(null); } } : {})}
      aria-label={onActivate ? t('coldChain.chart.details', 'Open cold point details') : t('coldChain.chart.label', 'Temperature chart')}>
      <defs>
        <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" strokeWidth="2" className="stroke-slate-300 dark:stroke-slate-600" />
        </pattern>
      </defs>
      {chart.bands.map((bd, i) => (
        <rect key={`b${i}`} data-band x={bd.x} y={Math.min(bd.y1, bd.y2)} width={bd.w} height={Math.abs(bd.y2 - bd.y1)}
          className="fill-emerald-100/60 dark:fill-emerald-500/10" />
      ))}
      {chart.off.map((o, i) => (
        <rect key={`o${i}`} data-off x={o.x} y={plot.y} width={o.w} height={plot.h} fill={`url(#${hatchId})`} />
      ))}
      {chart.excursions.map((e, i) => (
        <rect key={`e${i}`} data-excursion={e.kind} x={e.x} y={plot.y} width={e.w} height={plot.h}
          className={e.kind === 'no_data' ? 'fill-slate-200/40 dark:fill-slate-700/30' : 'fill-rose-200/50 dark:fill-rose-500/20'} />
      ))}
      {chart.gaps.map((g, i) => (
        <rect key={`g${i}`} data-gap x={g.x} y={plot.y} width={g.w} height={plot.h} className="fill-slate-300/30 dark:fill-slate-600/30" />
      ))}
      {chart.segments.map((s, i) => (
        <g key={`s${i}`}>
          {s.envelope && <path d={s.envelope} className="fill-sky-200/50 dark:fill-sky-500/20" />}
          <path data-segment d={s.line} fill="none" strokeWidth={1.5} className="stroke-sky-600 dark:stroke-sky-400" />
          {s.dot && <circle cx={s.dot.x} cy={s.dot.y} r={2.5} className="fill-sky-600 dark:fill-sky-400" />}
        </g>
      ))}
      {chart.versionMarks.map((m, i) => (
        <line key={`v${i}`} data-version-mark={m.versionNo} x1={m.x} x2={m.x} y1={plot.y} y2={plot.y + plot.h}
          strokeDasharray="1 3" className="stroke-slate-400" />
      ))}
      {chart.doors.map((d, i) => (
        <line key={`d${i}`} data-door={d.open ? 'open' : 'closed'} x1={d.x} x2={d.x} y1={plot.y} y2={plot.y + plot.h}
          strokeDasharray="2 2" className={d.open ? 'stroke-amber-500' : 'stroke-slate-400'} />
      ))}
      {chart.moving.map((m, i) => (
        <rect key={`m${i}`} data-moving={m.kind} x={m.x} y={strip.y} width={m.w} height={strip.h}
          className={m.kind === 'moving' ? 'fill-blue-400' : 'fill-slate-300 dark:fill-slate-600'} />
      ))}
      {!compact && chart.yTicks.map((tk, i) => (
        <text key={`y${i}`} x={2} y={tk.y + 3} className="fill-slate-400 text-[9px]">{tk.label}</text>
      ))}
      {!compact && tz && (
        <>
          <text x={plot.x} y={chart.height - 2} textAnchor="start" className="fill-slate-400 text-[9px]">{dateTimeInTz(new Date(history.fromMs).toISOString(), tz)}</text>
          <text x={plot.x + plot.w} y={chart.height - 2} textAnchor="end" className="fill-slate-400 text-[9px]">{dateTimeInTz(new Date(history.toMs).toISOString(), tz)}</text>
        </>
      )}
      {probe && <line data-crosshair x1={probe.vx} x2={probe.vx} y1={plot.y} y2={plot.y + plot.h} className="stroke-slate-500" strokeWidth={1} pointerEvents="none" />}
    </svg>
  );
  if (!interactive) return svg;
  return (
    <div ref={wrapRef} className={`relative ${onActivate ? 'cursor-pointer' : ''}`}>
      {svg}
      {probe && (
        <div data-testid="cold-chart-tooltip" role="status" dir="ltr" style={{ left: `${pct}%`, transform: `translateX(${tipShift})` }}
          className="pointer-events-none absolute top-0 z-10 max-w-full whitespace-nowrap rounded-lg bg-slate-900/90 px-2 py-1 text-[11px] text-white shadow-lg">
          <bdi>{dateTimeInTz(new Date(probe.atMs).toISOString(), zone)}</bdi>
          {' · '}
          <bdi className="font-semibold">{tipText}</bdi>
          {doorText && <span className="ms-1 text-amber-300">{doorText}</span>}
        </div>
      )}
    </div>
  );
}
